// Filtros tipados de los listados (#247). Fuente única del contrato de parámetros del
// cliente: los nombres son los MISMOS en el API y en la URL del SPA (snake_case, un valor
// por parámetro), de modo que una URL se puede compartir y recargar tal cual.
//
// Con red, filtra el SERVIDOR (una consulta con la query string). Sin red confirmada
// (lib/online-status.ts es fail-closed al arrancar, #237) se filtra en el navegador sobre
// la lista base ya cacheada, con la función pura de la entidad. La regla de equivalencia
// (D2/R7) obliga a que esas funciones puras den el MISMO resultado que el SQL: cada una
// tiene la misma tabla de casos que su test de integración en Go.

import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { ApiError, api } from "@/lib/api";
import { queryDeFiltros } from "@/lib/export";
import { useEstadoConexion, useOnlineStatus } from "@/lib/online-status";

/** Filtros es el objeto de filtros tal como viaja en la URL: claves conocidas a texto. */
export type Filtros = Record<string, string | undefined>;

/**
 * AVISO_FILTRADO_LOCAL es el aviso discreto que muestran los listados cuando están
 * filtrando sin red sobre la copia guardada: lo que no aparece puede existir en el
 * servidor, y quien opera en planta tiene derecho a saberlo.
 */
export const AVISO_FILTRADO_LOCAL = "Sin conexión: filtros aplicados sobre los datos guardados";

/**
 * filtrosActivos se queda solo con los filtros que realmente acotan: descarta las claves
 * ausentes, nulas o con cadena vacía. Es lo que se manda al servidor, lo que cuenta el chip
 * «N filtros activos» y lo que entra en la query key.
 */
export function filtrosActivos(filtros: Filtros | undefined): Record<string, string> {
  const activos: Record<string, string> = {};
  if (!filtros) {
    return activos;
  }
  // Las claves se ordenan para que la query key sea ESTABLE: dos objetos con los mismos
  // filtros en distinto orden deben ser la misma entrada de caché.
  for (const clave of Object.keys(filtros).sort()) {
    const valor = filtros[clave];
    if (valor != null && valor !== "") {
      activos[clave] = valor;
    }
  }
  return activos;
}

/**
 * validadorDeFiltros construye el `validateSearch` de una ruta a partir de las claves que
 * ese listado admite: se queda con esas claves y descarta el resto, así que un parámetro
 * basura en la URL no llega ni a la petición ni al componente.
 *
 * Los valores se normalizan a TEXTO porque el contrato de parámetros lo es (D3), y porque
 * el decodificador de TanStack convierte por su cuenta `?area=10` en el número 10 y
 * `?activo=true` en el booleano true: sin esta normalización, una URL tecleada a mano
 * perdería justo los filtros numéricos y booleanos. Lo que no es un escalar (un objeto o un
 * array colados en la query) se descarta.
 */
export function validadorDeFiltros<K extends string>(
  claves: readonly K[],
): (search: Record<string, unknown>) => Partial<Record<K, string>> {
  return (search: Record<string, unknown>): Partial<Record<K, string>> => {
    const out: Partial<Record<K, string>> = {};
    for (const clave of claves) {
      const valor = search[clave];
      if (typeof valor === "string" || typeof valor === "number" || typeof valor === "boolean") {
        const texto = String(valor);
        if (texto !== "") {
          out[clave] = texto;
        }
      }
    }
    return out;
  };
}

/**
 * useFiltrosURL lee los filtros del search de la ruta actual y devuelve el emisor que los
 * reescribe. La navegación es `replace: true` (D10): teclear en el buscador no debe llenar
 * el historial de entradas intermedias. `strict: false` evita acoplar el hook a una ruta
 * concreta, igual que hace useObraId con los params.
 */
export function useFiltrosURL<K extends string>(
  claves: readonly K[],
): {
  filtros: Partial<Record<K, string>>;
  setFiltros: (nuevos: Partial<Record<K, string>>) => void;
} {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();

  // El objeto de search cambia de identidad en cada render del router. Se normaliza una vez
  // y se memoiza POR CONTENIDO, para no re-disparar en cada render lo que depende de los
  // filtros (el debounce del buscador y la consulta filtrada, entre otros).
  const serializado = JSON.stringify(validadorDeFiltros(claves)(search));
  const filtros = useMemo(
    () => JSON.parse(serializado) as Partial<Record<K, string>>,
    [serializado],
  );

  const setFiltros = useCallback(
    (nuevos: Partial<Record<K, string>>) => {
      // Sin `to`, la navegación se queda en la ruta actual, y el search se REEMPLAZA entero
      // (los filtros que se quitan desaparecen de la URL, no se arrastran). El cast es
      // inevitable: este hook es deliberadamente agnóstico de la ruta —como useObraId con
      // los params—, así que el `search` tipado de esa ruta no puede inferirse aquí.
      navigate({ search: filtrosActivos(nuevos as Filtros) as never, replace: true });
    },
    [navigate],
  );

  return { filtros, setFiltros };
}

/**
 * ResultadoLista es lo que devuelve useListaFiltrada a la página. Conserva los nombres de
 * TanStack Query (`data`, `isPending`, `isLoading`, `isSuccess`, `isError`) para que los
 * llamantes que solo quieren la lista —formularios, asistentes, diálogos— no noten el cambio.
 */
export type ResultadoLista<T> = {
  /** data son las filas ya filtradas (por el servidor o por la función pura). */
  data: T[] | undefined;
  /** base es la lista SIN filtrar, fuente de las opciones de los desplegables. */
  base: T[] | undefined;
  isPending: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  /**
   * codigoError es el `code` del sobre de error del API cuando la consulta falló. La página
   * lo necesita para separar «los filtros no se sostienen» (FILTRO_INVALIDO, se corrige
   * cambiándolos) de «el servidor falló» (se corrige reintentando).
   */
  codigoError: string | undefined;
  /** camposError son los `fields` de ese sobre: qué filtro concreto hay que revisar. */
  camposError: Record<string, string> | undefined;
  /**
   * baseVacia dice si el listado está vacío DE VERDAD: la lista base resolvió sin filas.
   * Mientras la base está en vuelo es `false` —«no se sabe» no es «no hay»—, para que la
   * página no anuncie «Aún no hay…» escondiendo las filas que la consulta filtrada sí trajo.
   */
  baseVacia: boolean;
  /** filtradoLocal es true cuando se está filtrando en el navegador sobre lo guardado. */
  filtradoLocal: boolean;
  /**
   * avisoSinConexion dice si se le puede AFIRMAR al usuario que está sin conexión. No
   * coincide con `filtradoLocal`: el arranque es fail-closed (#237) y filtra en local
   * mientras el heartbeat no confirma, pero ahí la conectividad está «sin confirmar» y el
   * indicador de la cabecera dice «Comprobando…». Afirmar «Sin conexión» en ese hueco sería
   * contradecirlo con algo que todavía no se sabe.
   */
  avisoSinConexion: boolean;
};

/** OpcionesLista son los parámetros de useListaFiltrada. */
export type OpcionesLista<T> = {
  /** claveBase es la query key de la lista completa, p. ej. ["obra", 1, "equipos"]. */
  claveBase: readonly unknown[];
  /** url es la ruta del listado sin query, p. ej. "/obras/1/equipos". */
  url: string;
  /** filtros son los filtros de la URL (se normalizan con filtrosActivos). */
  filtros: Filtros;
  /** filtrar es la función pura de la entidad, usada solo en el modo sin red. */
  filtrar: (filas: T[], filtros: Record<string, string>) => T[];
};

/**
 * useListaFiltrada resuelve un listado con filtros (D9). Mantiene SIEMPRE suscrita la lista
 * base —es la unidad de caché offline y la fuente de las opciones— y, cuando hay red y
 * filtros activos, añade la consulta filtrada bajo la clave `[...claveBase, filtros]`. Sin
 * red confirmada no se pide nada: se filtra en el navegador sobre la lista base cacheada.
 *
 * La clave filtrada cuelga de la base a propósito: las invalidaciones por prefijo que ya
 * hacen las mutaciones (`invalidateQueries({queryKey: claveBase})`) alcanzan también a las
 * variantes filtradas, sin tocar ni un caller. Y `shouldPersistQuery` la descarta por
 * llevar un objeto en la posición 3 (D8): en disco solo vive la lista base.
 */
export function useListaFiltrada<T>({
  claveBase,
  url,
  filtros,
  filtrar,
}: OpcionesLista<T>): ResultadoLista<T> {
  const online = useOnlineStatus();
  const estadoConexion = useEstadoConexion();
  const activos = useMemo(() => filtrosActivos(filtros), [filtros]);
  const conFiltros = Object.keys(activos).length > 0;
  // Con red y filtros manda el servidor; en cualquier otro caso basta la lista base.
  const alServidor = online && conFiltros;

  const base = useQuery({
    queryKey: claveBase,
    queryFn: () => api.get<T[]>(url),
  });

  const filtrada = useQuery({
    queryKey: [...claveBase, activos],
    queryFn: () => api.get<T[]>(`${url}${queryDeFiltros(activos)}`),
    enabled: alServidor,
  });

  const filtradoLocal = !online && conFiltros;
  const locales = useMemo(
    () => (filtradoLocal && base.data ? filtrar(base.data, activos) : undefined),
    [filtradoLocal, base.data, activos, filtrar],
  );

  // «Vacío» solo se puede afirmar con la base RESUELTA: `undefined` es «aún no se sabe».
  const baseVacia = base.data != null && base.data.length === 0;
  // El aviso se afirma solo con la conectividad CONFIRMADA caída (#237); durante
  // «Comprobando…» ya se filtra en local, pero todavía no hay nada que afirmar.
  const avisoSinConexion = conFiltros && estadoConexion === "offline";

  if (alServidor) {
    const { code, fields } = detalleError(filtrada.error);
    return {
      data: filtrada.data,
      base: base.data,
      isPending: filtrada.isPending,
      isLoading: filtrada.isLoading,
      isSuccess: filtrada.isSuccess,
      isError: filtrada.isError,
      codigoError: code,
      camposError: fields,
      baseVacia,
      filtradoLocal: false,
      avisoSinConexion: false,
    };
  }
  const { code, fields } = detalleError(base.error);
  return {
    data: filtradoLocal ? locales : base.data,
    base: base.data,
    isPending: base.isPending,
    isLoading: base.isLoading,
    isSuccess: base.isSuccess,
    isError: base.isError,
    codigoError: code,
    camposError: fields,
    baseVacia,
    filtradoLocal,
    avisoSinConexion,
  };
}

/** detalleError desempaqueta el sobre tipado del API; nada si el error es de otro tipo. */
function detalleError(error: unknown): {
  code: string | undefined;
  fields: Record<string, string> | undefined;
} {
  if (error instanceof ApiError) {
    return { code: error.code, fields: error.fields };
  }
  return { code: undefined, fields: undefined };
}

/**
 * ETIQUETA_FILTRO nombra cada parámetro en el idioma de la pantalla, para que el 400 del
 * servidor señale un control que el usuario reconoce y no una clave de la query.
 */
const ETIQUETA_FILTRO: Record<string, string> = {
  q: "búsqueda",
  area: "área",
  equipo: "equipo",
  fuente: "fuente",
  estado: "estado",
  criticidad: "criticidad",
  activo: "activo",
  es_admin: "Admin",
  loteable: "loteable",
  tipo: "tipo",
  version: "versión",
  desde: "abierto desde",
  hasta: "abierto hasta",
  cierre_desde: "cerrado desde",
  cierre_hasta: "cerrado hasta",
  creado_por: "quién lo abrió",
  obra: "obra",
  rol: "rol",
  invitacion: "invitación",
  sso: "Microsoft",
};

/**
 * mensajeErrorLista compone el aviso de un listado que no se pudo cargar. Un 400
 * FILTRO_INVALIDO NO es un fallo del servidor: es una combinación de filtros que no se
 * sostiene, y lo accionable es corregirla —por eso se nombra el filtro culpable— y no
 * «volver a intentarlo», que repetiría el mismo error para siempre. Cualquier otro fallo
 * conserva el mensaje genérico de la página.
 */
export function mensajeErrorLista(
  { codigoError, camposError }: Pick<ResultadoLista<unknown>, "codigoError" | "camposError">,
  generico: string,
): string {
  if (codigoError !== "FILTRO_INVALIDO") {
    return generico;
  }
  const detalles = Object.entries(camposError ?? {}).map(
    ([campo, motivo]) => `${ETIQUETA_FILTRO[campo] ?? campo}: ${motivo}`,
  );
  return detalles.length > 0
    ? `Revisa los filtros — ${detalles.join(" · ")}.`
    : "Revisa los filtros: la combinación seleccionada no es válida.";
}
